import {useState} from "react";
import {Image, Platform, TouchableOpacity} from "react-native";
import {useAnalytics} from "@unibuddy/tracking";
import {
    Column,
    Columns,
    Heading,
    LoadingButton,
    Stack,
    Text,
    usePatronTheme,
    View,
} from "@unibuddy/patron";
import {IconXMark} from "@unibuddy/icons";
import {Link} from "ubcommunity-shared/src/General/Link/Link";
import {Routes} from "ubcommunity-shared/src/constants/routes";
import {UniversityField, useUpdateUniversityLinkMutation} from "../types";
import {SpaceType, useSpaces} from "../StudentApp/SpacesProvider";
import {MutationErrorHandler} from "../General/Errors/MutationErrorHandler";
import {DiscoverEnterDialogAnimation} from "./DiscoverEnterDialogAnimation";
import {TrackEvents} from "../constants";
import {useNavigate} from "../Hooks/useNavigate";
import {useIsFeatureEnabledBoolean} from "../Hooks/useIsFeatureEnabledBoolean";
import {FeatureFlags} from "../featureFlags";
import CheckboxSvg from "../Svgs/CheckboxSvg";

type DiscoverEnterDialogProps = {
    university: UniversityField | null;
    onClose: () => void;
};

export enum DiscoverEnterDialogStrings {
    ButtonText = "Start Chatting",
    Title = "Join conversations with our current students and staff members",
    AgreeToTerms = "By clicking continuing you are agreeing to this",
    Error = "An error occurred! Please try again later.",
    MarketingConsentLabel = "Consent to marketing communication",
}

export const DiscoverEnterDialog = ({university, onClose}: DiscoverEnterDialogProps) => {
    const {trackEvent} = useAnalytics();
    const [loading, setLoading] = useState(false);
    const [marketing, setMarketing] = useState(true);
    const [updateLink, {error}] = useUpdateUniversityLinkMutation();
    const {refetch: refetchSpaces} = useSpaces();
    const {navigate} = useNavigate();
    const theme = usePatronTheme();
    const isEnhancedRedirectEnabled = useIsFeatureEnabledBoolean(
        FeatureFlags.STUDENT_APP_ENHANCED_LEADS_REDIRECT,
    );

    if (!university) return null;

    const handleLink = async () => {
        trackEvent(TrackEvents.DISCOVER_UNIVERSITY_LINK_CREATION_STARTED, {
            universityId: university.id,
            universityName: university.name,
            universitySlug: university.slug,
        });
        setLoading(true);
        try {
            await updateLink({
                variables: {
                    universitySlug: university.slug,
                    sourceTrackingParams: JSON.stringify({
                        ub_source: "Unibuddy App",
                    }),
                    acceptMarketing: marketing,
                },
            });
            trackEvent(TrackEvents.DISCOVER_UNIVERSITY_LINK_CREATION_COMPLETED, {
                universityId: university.id,
                universityName: university.name,
                universitySlug: university.slug,
            });

            if (isEnhancedRedirectEnabled) {
                navigate({
                    path: Routes.SIGNUP_STEP_2,
                    options: {
                        id: university.id,
                    },
                });
            } else {
                await refetchSpaces();
                navigate({
                    path: Routes.HOME,
                    options: {
                        selected: university.slug,
                        type: SpaceType.UNIVERSITY,
                    },
                });
            }
            onClose();
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    const Checkbox = Platform.OS === "web" ? "button" : TouchableOpacity;

    return (
        <View
            position="absolute"
            zIndex={1}
            w="100%"
            h="100%"
            justifyContent="center"
            alignItems="center"
        >
            <DiscoverEnterDialogAnimation trigger={!!university}>
                <View w="100%" alignItems="flex-end">
                    <TouchableOpacity
                        accessibilityRole="button"
                        accessibilityLabel="Close Dialog"
                        onPress={onClose}
                    >
                        <IconXMark />
                    </TouchableOpacity>
                </View>
                <Stack space="medium-large" align="center">
                    <Image
                        style={{width: 80, height: 80, borderRadius: 6}}
                        source={{uri: university.squareLogo ?? ""}}
                        width={80}
                        height={80}
                    />
                    <Heading level="3" size="xsmall" align="center">
                        {university.name}
                    </Heading>
                    <Text align="center" size="small" color="grey800">
                        {DiscoverEnterDialogStrings.Title}
                    </Text>
                    <View paddingTop="small">
                        <Stack space="large">
                            <Checkbox
                                onPress={() => setMarketing(!marketing)}
                                onClick={() => setMarketing(!marketing)}
                                accessible={true}
                                accessibilityRole="checkbox"
                                accessibilityLabel={
                                    DiscoverEnterDialogStrings.MarketingConsentLabel
                                }
                                accessibilityState={{checked: marketing}}
                                aria-checked={marketing}
                                role="checkbox"
                                aria-label={DiscoverEnterDialogStrings.MarketingConsentLabel}
                                style={{
                                    all: "unset",
                                    cursor: "pointer",
                                }}
                            >
                                <Columns space="small">
                                    <Column width="content">
                                        <CheckboxSvg
                                            color={
                                                marketing
                                                    ? theme.colors.primaryColor
                                                    : theme.colors.grey600
                                            }
                                            filled={marketing}
                                        />
                                    </Column>
                                    <Column>
                                        <Text color="grey600" size="xsmall">
                                            By checking this box, you agree to receive marketing
                                            communication. You can unsubscribe at any time.
                                        </Text>
                                    </Column>
                                </Columns>
                            </Checkbox>
                            <Stack space="medium">
                                <LoadingButton
                                    block
                                    color="primary"
                                    loading={loading}
                                    onClick={handleLink}
                                >
                                    <Text color="white">
                                        {DiscoverEnterDialogStrings.ButtonText}
                                    </Text>
                                </LoadingButton>
                                {error ? (
                                    <MutationErrorHandler
                                        error={error}
                                        message={DiscoverEnterDialogStrings.Error}
                                    />
                                ) : null}
                                <Text align="center" size="xxsmall">
                                    {DiscoverEnterDialogStrings.AgreeToTerms}{" "}
                                    <Link
                                        external
                                        href={university.privacyPolicyUrl ?? ""}
                                        style={{
                                            textDecorationLine: "underline",
                                        }}
                                        size="xsmall"
                                    >
                                        institution's T&Cs
                                    </Link>
                                </Text>
                            </Stack>
                        </Stack>
                    </View>
                </Stack>
            </DiscoverEnterDialogAnimation>
        </View>
    );
};
